import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'

/** Tipo de notificación: da el icono y el color en la campana. */
export type TipoNotificacion = 'aplazamiento' | 'incidencia' | 'aviso'

/** Una notificación de la campana del header. */
export interface Notificacion {
  id: string
  tipo: TipoNotificacion
  titulo: string
  cuerpo: string | null
  enlace: string | null
  contactId: string | null
  leida: boolean
  creadaEn: string
}

interface RespuestaNotificaciones {
  items: Notificacion[]
  total: number
}

interface RespuestaContador {
  noLeidas: number
}

/**
 * Lista las notificaciones recientes. Se refresca sola cada 30 s para que la
 * campana muestre lo que va ocurriendo (aplazamientos, incidencias, avisos) sin
 * recargar la página.
 */
export function useNotificaciones() {
  const api = useApi()
  return useQuery<RespuestaNotificaciones, ErrorApi>({
    queryKey: ['notificaciones'],
    queryFn: ({ signal }) =>
      api.pedir<RespuestaNotificaciones>('/api/notificaciones', { senal: signal }),
    refetchInterval: 30_000,
  })
}

/**
 * Lista AMPLIA para la página "Centro de alertas" (/alertas): trae muchas más que
 * la campana (que se queda en 50) para poder recorrer el histórico por fecha. El
 * `queryKey` lleva el límite para no pisar la caché de la campana.
 */
export function useTodasLasNotificaciones(limite = 500) {
  const api = useApi()
  return useQuery<RespuestaNotificaciones, ErrorApi>({
    queryKey: ['notificaciones', 'todas', limite],
    queryFn: ({ signal }) =>
      api.pedir<RespuestaNotificaciones>(`/api/notificaciones?limite=${limite}`, { senal: signal }),
    refetchInterval: 30_000,
  })
}

/** Contador de no leídas (el badge rojo). Refresco corto para que el número vaya al día. */
export function useContadorNoLeidas() {
  const api = useApi()
  return useQuery<RespuestaContador, ErrorApi>({
    queryKey: ['notificaciones', 'contador'],
    queryFn: ({ signal }) =>
      api.pedir<RespuestaContador>('/api/notificaciones/no-leidas/contador', { senal: signal }),
    refetchInterval: 30_000,
  })
}

/** Refresca lista + contador tras cualquier mutación. */
function useRefrescar() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['notificaciones'] })
  }
}

/** Marca una notificación como leída. */
export function useMarcarLeida() {
  const api = useApi()
  const refrescar = useRefrescar()
  return useMutation<unknown, ErrorApi, string>({
    mutationFn: (id) => api.pedir(`/api/notificaciones/${id}/leer`, { metodo: 'POST' }),
    onSuccess: refrescar,
  })
}

/** Marca todas como leídas ("Marcar todas"). */
export function useMarcarTodasLeidas() {
  const api = useApi()
  const refrescar = useRefrescar()
  return useMutation<unknown, ErrorApi, void>({
    mutationFn: () => api.pedir('/api/notificaciones/leer-todas', { metodo: 'POST' }),
    onSuccess: refrescar,
  })
}

/** Borra una notificación (la X de la tarjeta). */
export function useEliminarNotificacion() {
  const api = useApi()
  const refrescar = useRefrescar()
  return useMutation<unknown, ErrorApi, string>({
    mutationFn: (id) => api.pedir(`/api/notificaciones/${id}`, { metodo: 'DELETE' }),
    onSuccess: refrescar,
  })
}

/** Vacía la bandeja entera (la papelera). */
export function useEliminarTodas() {
  const api = useApi()
  const refrescar = useRefrescar()
  return useMutation<unknown, ErrorApi, void>({
    mutationFn: () => api.pedir('/api/notificaciones', { metodo: 'DELETE' }),
    onSuccess: refrescar,
  })
}
